import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { sum as d3sum } from 'd3';

import { Comp, getContainerStylingTransientProps } from '@redsift/design-system';

import { LegendProps } from './types';
import { StyledLegend } from './styles';
import { LegendVariant } from '../../types';
import { LegendItem } from '../LegendItem';

const COMPONENT_NAME = 'Legend';
const CLASSNAME = 'redsift-chart-legend';

export const Legend: Comp<LegendProps, HTMLUListElement> = forwardRef((props, ref) => {
  const { width, ...propsWithoutWidth } = props;
  const { transientProps, otherProps } = getContainerStylingTransientProps(propsWithoutWidth);

  const {
    children,
    className,
    data,
    isLegendItemSelected = () => true,
    labelDecorator,
    legendDecorator,
    legendItemRole,
    onLegendItemClick,
    variant = LegendVariant.label,
    ...forwardedProps
  } = otherProps;

  if (!data || data.length === 0) {
    return null;
  }

  const total = d3sum(data, (d) => d.value);

  if (children) {
    const renderedChildren = typeof children === 'function' ? children(data, total) : children;

    return (
      <StyledLegend
        {...forwardedProps}
        {...transientProps}
        className={classNames(Legend.className, className)}
        ref={ref}
      >
        {renderedChildren}
      </StyledLegend>
    );
  }

  return (
    <StyledLegend
      {...forwardedProps}
      {...transientProps}
      className={classNames(Legend.className, className)}
      ref={ref}
      $width={width}
    >
      {data.map(({ color, ...datum }, index) => (
        <LegendItem
          data={{ data: { ...datum, percent: datum.percent ?? datum.value / total } }}
          dataset={data}
          color={color}
          variant={variant}
          index={index}
          isSelected={Boolean(isLegendItemSelected!({ data: datum }))}
          key={`legend-item _${index}`}
          labelDecorator={labelDecorator}
          legendDecorator={legendDecorator}
          onClick={onLegendItemClick}
          role={legendItemRole}
        />
      ))}
    </StyledLegend>
  );
});
Legend.className = CLASSNAME;
Legend.displayName = COMPONENT_NAME;
